import React, { useMemo } from 'react';
import './LoaderAnimation.scss';

// Circle configurations with their default positions and sizes (static)
const circles = [
    { id: 1, size: 61.5, left: 94.5, top: 163.0, color: 'primary' },
    { id: 2, size: 61.5, left: 160.0, top: 98.5, color: 'primary' },
    { id: 3, size: 46.5, left: 102.0, top: 233.0, color: 'primary' },
    { id: 4, size: 46.5, left: 168.0, top: 43.0, color: 'primary' },
    { id: 5, size: 38.8, left: 106.0, top: 108.5, color: 'secondary' },
    { id: 6, size: 38.8, left: 171.5, top: 174.0, color: 'secondary' },
    { id: 7, size: 46.5, left: 39.5, top: 170.5, color: 'primary' },
    { id: 8, size: 46.5, left: 230.0, top: 105.0, color: 'primary' },
    { id: 9, size: 31.2, left: 47.5, top: 112.5, color: 'secondary' },
    { id: 10, size: 31.2, left: 238.0, top: 178.0, color: 'secondary' },
    { id: 11, size: 31.2, left: 110.0, top: 50.0, color: 'secondary' },
    { id: 12, size: 31.2, left: 175.5, top: 240.0, color: 'secondary' },
];

export const LoaderAnimation = ({
    size = null, // set to 'small', 'medium', or 'large'
    sizeValue = 75,
    className = '',
    color = null, // set to 'grayscale', 'whiteBackground', or 'blueBackground'
    colorPrimary = 'var(--loader-primary-white-background)',
    colorSecondary = 'var(--loader-secondary-white-background)',
}) => {
    // Memoize actualSize calculation
    const actualSize = useMemo(() => {
        if (size === 'small') return 75;
        else if (size === 'medium') return 150;
        else if (size === 'large') return 225;
        else {
            console.log('LoaderAnimation: Invalid size argument, using default size value.');
            return sizeValue;
        }
    }, [size, sizeValue]);

    // Memoize color calculation
    const [primaryColor, secondaryColor] = useMemo(() => {
        if (color === 'grayscale') {
            return ['var(--loader-primary-grayscale)', 'var(--loader-secondary-grayscale)'];
        } else if (color === 'whiteBackground') {
            return ['var(--loader-primary-white-background)', 'var(--loader-secondary-white-background)'];
        } else if (color === 'blueBackground') {
            return ['var(--loader-primary-blue-background)', 'var(--loader-secondary-blue-background)'];
        } else {
            console.log('LoaderAnimation: Invalid color argument, using default colors.');
        }
        return [colorPrimary, colorSecondary];
    }, [color, colorPrimary, colorSecondary]);

    // Memoize scale
    const scale = useMemo(() => actualSize / 200, [actualSize]);

    // Memoize container style
    const containerStyle = useMemo(
        () => ({
            width: `${actualSize}px`,
            height: `${actualSize}px`,
            position: 'relative',
            margin: '0 auto',
            '--scale-factor': scale,
        }),
        [actualSize, scale],
    );

    return (
        <div
            className={`loader-animation ${className}`}
            style={containerStyle}
            data-color-primary={primaryColor}
            data-color-secondary={secondaryColor}
        >
            {circles.map((circle) => (
                <div
                    key={circle.id}
                    className={`loader-circle loader-circle-${circle.id}`}
                    style={{
                        width: `${circle.size * scale}px`,
                        height: `${circle.size * scale}px`,
                        left: `${circle.left * scale}px`,
                        top: `${circle.top * scale}px`,
                        position: 'absolute',
                        borderRadius: '50%',
                        background: circle.color === 'primary' ? primaryColor : secondaryColor,
                    }}
                />
            ))}
        </div>
    );
};

export default LoaderAnimation;
